import { useQuery } from '@tanstack/react-query';
import { useParams } from '@tanstack/react-router';
import type { PublicWebFormConfigResponse } from '@orion/contracts';
import { Card } from '@orion/ui';
import { ApiError, api } from '../../lib/api-client.js';
import { DynamicLeadForm } from './DynamicLeadForm.js';

function usePublicWebFormQuery(publicKey: string) {
  return useQuery({
    queryKey: ['public-web-form', publicKey],
    queryFn: () => api.get<PublicWebFormConfigResponse>(`/public/forms/${publicKey}`),
    retry: false,
  });
}

/** A branded, full-page landing spot for a Forms-module form (docs section
 * 9 "Lead Capture") — a real visitor's browser hits this directly, e.g. a
 * link from an email campaign or a "Contact us" button, distinct from
 * `EmbedWebFormPage` which strips the branded chrome for an iframe. Every
 * bit of copy/colour/logo comes from the form's own config — nothing here
 * is hardcoded per-tenant. */
export function PublicWebFormPage() {
  const { publicKey } = useParams({ from: '/forms/$publicKey' });
  const { data, isLoading, isError, error } = usePublicWebFormQuery(publicKey);

  if (isLoading) {
    return <div className="grid min-h-screen place-items-center bg-ground" />;
  }

  if (isError || !data) {
    return (
      <div className="grid min-h-screen place-items-center bg-ground px-4">
        <div className="w-full max-w-[440px] rounded-card border border-border bg-surface p-8 text-center shadow-popover">
          <h1 className="text-xl font-semibold text-ink">Form not available</h1>
          <p className="mt-2 text-sm text-text-muted">
            {error instanceof ApiError && error.status === 404
              ? "This form doesn't exist or has been turned off."
              : "Something went wrong loading this form. Please try again shortly."}
          </p>
        </div>
      </div>
    );
  }

  const { branding } = data;

  return (
    <div className="flex min-h-screen bg-ground">
      <aside
        aria-hidden="true"
        className="relative hidden w-[420px] shrink-0 flex-col justify-between overflow-hidden bg-night p-10 lg:flex"
      >
        <svg viewBox="0 0 600 900" preserveAspectRatio="xMidYMid slice" className="pointer-events-none absolute inset-0 size-full opacity-90">
          <g stroke="#2A303C" strokeWidth="1.2" strokeLinecap="round">
            <path d="M316 104 204 168" />
            <path d="M316 104 432 196" />
            <path d="M204 168 284 316" />
            <path d="M432 196 380 284" />
            <path d="M284 316 236 452" />
            <path d="M380 284 464 436" />
          </g>
          <g fill="#F2B25C">
            <circle cx="284" cy="316" r="12" fillOpacity="0.12" />
            <circle cx="380" cy="284" r="12" fillOpacity="0.12" />
            <circle cx="284" cy="316" r="4" />
            <circle cx="380" cy="284" r="4" />
          </g>
        </svg>

        <div className="relative flex items-center gap-3">
          <span className="flex size-9 items-center justify-center overflow-hidden rounded-[10px] bg-night-surface">
            {branding.logoUrl ? (
              <img src={branding.logoUrl} alt={branding.productName} className="size-full object-contain p-1" />
            ) : (
              <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
                <circle cx="6.5" cy="16" r="2" fill="#F2B25C" />
                <circle cx="12" cy="12" r="2" fill="#F2B25C" />
                <circle cx="17.5" cy="8" r="2" fill="#F2B25C" />
              </svg>
            )}
          </span>
          <span className="text-lg font-semibold tracking-tight text-white">{branding.productName}</span>
        </div>

        <div className="relative flex flex-col gap-4">
          <h1 className="font-sans text-[36px] leading-[1.15] font-normal text-white">Let's start the conversation.</h1>
          <p className="text-[15px] leading-6 text-[#B7BCC7]">
            Tell us a bit about what you need — a real person on our team will follow up personally, usually within one
            business day.
          </p>
        </div>

        <div className="relative flex items-center gap-2 text-xs text-[#8A909C]">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#8A909C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M12 3 4 6v6c0 5 3.5 8 8 9 4.5-1 8-4 8-9V6z" />
          </svg>
          Your information is kept private and never shared.
        </div>
      </aside>

      <main className="flex flex-1 items-center justify-center px-4 py-10">
        <Card className="w-full max-w-[440px] p-8">
          <div className="mb-1 flex items-center gap-2 lg:hidden">
            {branding.logoUrl && <img src={branding.logoUrl} alt={branding.productName} className="size-6 object-contain" />}
            <span className="text-sm font-semibold text-ink">{branding.productName}</span>
          </div>
          <h2 className="text-xl font-semibold text-ink">{data.name}</h2>
          <p className="mb-6 mt-1 text-sm text-text-muted">Fields marked required must be filled in before you can submit.</p>
          <DynamicLeadForm publicKey={publicKey} config={data} />
        </Card>
      </main>
    </div>
  );
}
